// packages/whiteboard/src/optionsbar/parts/compose-options-circle.ts
//
// Setzt aus den Einzelteilen (part1 … part10) den kompletten Optionskreis
// eines Werkzeugs zusammen.
//
// Ausrichtung — die „Passmarke":
//   Jedes Teil trägt oben rechts ein winziges Rechteck `<g id="platzierung">`.
//   Beim Zusammenbauen werden die Teile so verschoben, dass ihre Passmarken
//   deckungsgleich liegen. Da alle Passmarken bei y=0 sitzen, ist nur eine
//   horizontale Verschiebung nötig: jedes Teil rückt um
//   `(rechteste Passmarke − eigene Passmarke)` nach rechts. Die Leinwand ist
//   dann `rechteste Passmarke + 1,5` breit und so hoch wie das höchste Teil.
//   Das reproduziert exakt die früheren Einzel-SVGs (z. B. rectangle 200,8×220,
//   Stand Grafik-Update 2026-08).

import { OPTIONS_CIRCLE_PARTS } from './options-circle-parts.js';

/** Breite/Höhe des Passmarken-Rechtecks (siehe partN.svg). */
const MARKER_SIZE = 1.5;

interface ParsedPart {
  /** Der eigentliche Nutz-Inhalt: `<g id="partN">…</g>` (Passmarke NICHT enthalten). */
  readonly group: string;
  /** Etwaige `<defs>` des Teils (z. B. Schatten-Filter in part2), sonst ''. */
  readonly defs: string;
  /** x-Position der Passmarke — der Anker, an dem ausgerichtet wird. */
  readonly markerX: number;
  /** Höhe laut viewBox — bestimmt die Leinwandhöhe. */
  readonly height: number;
}

/** Auf eine Nachkommastelle runden (Passmarken sind so genau angegeben). */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Schneidet die balancierte Gruppe `<g id="{id}">…</g>` heraus — inklusive
 * korrekt verschachtelter `<g>`-Ebenen. So wird bei part2 nur die echte
 * `part2`-Gruppe genommen und ein evtl. nachgestelltes Rest-Stück (z. B. ein
 * verwaistes `<g id="part1">`) sauber ignoriert.
 */
function extractGroup(svg: string, id: string): string {
  const opener = `<g id="${id}">`;
  const start = svg.indexOf(opener);
  if (start < 0) throw new Error(`compose-options-circle: Gruppe "${id}" nicht gefunden`);

  const token = /<g\b[^>]*>|<\/g>/g;
  token.lastIndex = start;
  let depth = 0;
  let match: RegExpExecArray | null;
  while ((match = token.exec(svg)) !== null) {
    if (match[0].startsWith('</g')) {
      depth -= 1;
      if (depth === 0) return svg.slice(start, token.lastIndex);
    } else {
      depth += 1;
    }
  }
  throw new Error(`compose-options-circle: Gruppe "${id}" ist nicht balanciert`);
}

/** `<defs>…</defs>` des Teils (oder '' wenn keins vorhanden). */
function extractDefs(svg: string): string {
  const match = svg.match(/<defs>[\s\S]*?<\/defs>/);
  return match ? match[0] : '';
}

function parsePart(id: number): ParsedPart {
  const svg = OPTIONS_CIRCLE_PARTS[id];
  if (svg === undefined) throw new Error(`compose-options-circle: unbekanntes Teil "${id}"`);

  const viewBox = svg.match(/viewBox="0 0 ([0-9.]+) ([0-9.]+)"/);
  if (!viewBox) throw new Error(`compose-options-circle: Teil "${id}" ohne viewBox`);
  const width = parseFloat(viewBox[1]!);
  const height = parseFloat(viewBox[2]!);

  const marker = svg.match(/<g id="platzierung">\s*<rect x="([0-9.]+)"/);
  // Fallback: rechte Kante minus Markengröße, falls die Marke einmal fehlt.
  const markerX = marker ? parseFloat(marker[1]!) : width - MARKER_SIZE;

  return {
    group: extractGroup(svg, `part${id}`),
    defs: extractDefs(svg),
    markerX,
    height,
  };
}

/**
 * Baut den kompletten Optionskreis eines Werkzeugs aus den angegebenen Teilen.
 * Reihenfolge = Zeichenreihenfolge (erstes Teil unten, letztes oben).
 *
 * @param partIds z. B. `[1, 2, 3, 5]` für das Rechteck.
 * @returns fertiges SVG als String (wie zuvor `optionskreis-*.svg?raw`).
 */
export function composeOptionsCircle(partIds: readonly number[]): string {
  if (partIds.length === 0) throw new Error('compose-options-circle: keine Teile angegeben');

  const parts = partIds.map(parsePart);
  const xRef = Math.max(...parts.map((p) => p.markerX));
  const width = round1(xRef + MARKER_SIZE);
  const height = round1(Math.max(...parts.map((p) => p.height)));

  const defs = parts.map((p) => p.defs).join('');
  const body = parts
    .map((p) => {
      const dx = round1(xRef - p.markerX);
      return dx === 0 ? p.group : `<g transform="translate(${dx} 0)">${p.group}</g>`;
    })
    .join('');

  // `width`/`height` = natürliche Größe (1 Einheit = 1 px). So kann der
  // Optionsbar sie auslesen und alle Kreise mit demselben px-pro-Einheit-Faktor
  // skalieren — dadurch bleiben die Knöpfe über alle Werkzeuge gleich groß.
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    defs +
    body +
    `</svg>`
  );
}
